import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, FileDown, Search, Receipt, CheckCircle2, Clock, AlertTriangle, Camera } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InvoiceSheet } from "@/components/invoices/InvoiceSheet";
import { InvoiceExport } from "@/components/invoices/InvoiceExport";
import { ScanDialog } from "@/components/invoices/ScanDialog";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";
const FILTERS = [["", "Toutes"], ["a_regler", "À régler"], ["reglee", "Réglées"]];

function UploadZone({ onUploaded }) {
  const [paid, setPaid] = useState("a_regler");
  const [busy, setBusy] = useState(0);
  const [scan, setScan] = useState(false);
  const ref = useRef();
  const send = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(list.length);
    let ok = 0;
    const one = async (file) => {
      if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name} : 15 Mo maximum`); return; }
      const fd = new FormData(); fd.append("file", file); fd.append("status", paid);
      try { const { data } = await api.post("/invoices", fd, { timeout: 120000 }); ok += 1; onUploaded(data, list.length === 1); }
      catch (e) { toast.error(`${file.name} : ${apiErr(e)}`); }
    };
    const queue = [...list];
    await Promise.all([0, 1, 2].map(async () => { while (queue.length) { await one(queue.shift()); setBusy((b) => b - 1); } }));
    if (list.length > 1 && ok) toast.success(`${ok} facture(s) déposée(s), lecture en cours`);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className={`${CARD} p-5`} data-testid="invoice-upload-zone">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto text-sm font-semibold text-gray-700">Déposer une facture</p>
        {[["a_regler", "À régler"], ["reglee", "Déjà réglée"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`upload-status-${v}`} onClick={() => setPaid(v)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${paid === v ? "border-[#065F46] bg-[#065F46] text-white" : "border-gray-200 text-gray-600 hover:bg-emerald-50"}`}>{l}</button>
        ))}
      </div>
      <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); send(e.dataTransfer.files); }}
        className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 px-4 py-8 text-center transition-colors hover:bg-emerald-50">
        {busy > 0 ? <Loader2 className="h-7 w-7 animate-spin text-[#065F46]" /> : <Upload className="h-7 w-7 text-[#065F46]" />}
        <span className="mt-2 text-sm font-semibold text-[#065F46]">{busy > 0 ? `Envoi en cours (${busy})…` : "Cliquez ou glissez une ou plusieurs factures ici"}</span>
        <span className="mt-1 text-xs text-gray-500">Sélection multiple possible · PDF, photo (JPG, PNG, HEIC) · 15 Mo max par fichier · lecture automatique</span>
        <input ref={ref} data-testid="invoice-file-input" type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => send(e.target.files)} />
      </label>
      <Button type="button" variant="outline" onClick={() => setScan(true)} disabled={busy > 0} data-testid="invoice-scan-btn"
        className="mt-3 w-full rounded-full border-[#065F46] text-[#065F46] hover:bg-emerald-50"><Camera className="mr-2 h-4 w-4" />Scanner une facture avec l'appareil photo</Button>
      <ScanDialog open={scan} onOpenChange={setScan} onSend={(f) => send([f])} />
    </div>
  );
}

function Row({ inv, onOpen }) {
  const st = inv.status === "reglee";
  return (
    <button type="button" onClick={() => onOpen(inv)} data-testid={`invoice-row-${inv.id}`}
      className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-emerald-50/60">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-[#065F46]">
        {inv.extraction === "pending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{inv.supplier || inv.original_filename}</p>
        <p className="truncate text-xs text-gray-500">{inv.invoice_number ? `N° ${inv.invoice_number} · ` : ""}{inv.invoice_date ? fmtDate(inv.invoice_date) : "Date à préciser"}{inv.validated ? "" : " · à vérifier"}</p>
      </div>
      {inv.duplicate_of && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-label="Doublon possible" />}
      <div className="shrink-0 text-right">
        <p className="text-sm font-bold">{inv.amount_ttc != null ? fmtEUR2(inv.amount_ttc) : "—"}</p>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${st ? "bg-emerald-50 text-[#065F46]" : "bg-amber-50 text-amber-700"}`}>
          {st ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{st ? "Réglée" : "À régler"}
        </span>
      </div>
    </button>
  );
}

export default function Factures() {
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [status, setStatus] = useState("");
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [exp, setExp] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/invoices", { params: { status, month, q } }); setRows(data); }
    catch (e) { toast.error(apiErr(e)); setRows([]); }
  }, [status, month, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data.filter((c) => c.type === "expense" && !c.archived))).catch(() => {}); }, []);
  useEffect(() => {
    if (!rows?.some((r) => r.extraction === "pending")) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [rows, load]);
  useEffect(() => {
    if (open && rows) { const fresh = rows.find((r) => r.id === open.id); if (fresh && fresh.extraction !== open.extraction) setOpen(fresh); }
  }, [rows, open]);
  const uploaded = (inv, single) => { if (single) { toast.success("Facture déposée, lecture en cours"); setOpen(inv); } load(); };
  const todo = (rows || []).filter((r) => r.status === "a_regler").reduce((s, r) => s + (r.amount_ttc || 0), 0);
  return (
    <div className="space-y-6" data-testid="factures-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Finances</Label><h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Factures</h1>
          <p className="mt-1 text-sm text-gray-500">Déposez, vérifiez et archivez vos factures fournisseurs. Chaque facture validée alimente les Dépenses.</p></div>
        <Button data-testid="invoice-export-btn" onClick={() => setExp(true)} variant="outline" className="rounded-full"><FileDown className="mr-1 h-4 w-4" />Export comptable</Button>
      </div>
      <UploadZone onUploaded={uploaded} />
      <div className={`${CARD} p-4 sm:p-5`}>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([v, l]) => (
            <button key={l} type="button" data-testid={`invoice-filter-${v || "all"}`} onClick={() => setStatus(v)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${status === v ? "bg-[#065F46] text-white" : "bg-gray-50 text-gray-600 hover:bg-emerald-50"}`}>{l}</button>
          ))}
          <Input data-testid="invoice-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40 rounded-full" />
          <div className="relative min-w-[180px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input data-testid="invoice-search" placeholder="Fournisseur, n°, SIRET…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 rounded-full pl-9" /></div>
        </div>
        {rows && rows.length > 0 && <p className="mt-3 text-xs text-gray-500" data-testid="invoice-summary">{rows.length} facture(s) · reste à régler : <b className="text-amber-700">{fmtEUR2(todo)}</b></p>}
        <div className="mt-2 divide-y divide-gray-50">
          {rows === null ? <Loader /> : rows.length === 0
            ? <EmptyState title="Aucune facture" subtitle="Déposez votre première facture ci-dessus." />
            : rows.map((r) => <Row key={r.id} inv={r} onOpen={setOpen} />)}
        </div>
      </div>
      {open && <InvoiceSheet inv={open} cats={cats} onClose={() => setOpen(null)} onChanged={(d) => { load(); if (d) setOpen(d); }} />}
      <InvoiceExport open={exp} onOpenChange={setExp} />
    </div>
  );
}
